/**
 * ColumnHeaderMenuTrigger — Small dropdown trigger for column header actions.
 *
 * Renders a chevron button that opens a menu with Sort, Move, and Hide options.
 * Designed to be placed inside the column header without interfering with drag/drop.
 * Uses onPointerDown stopPropagation to prevent triggering dnd-kit drag handlers.
 */

import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronUp,
  EyeOff,
} from '@untitledui/icons';
import { useCallback } from 'react';
import {
  Button as AriaButton,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuTrigger as AriaMenuTrigger,
  Popover as AriaPopover,
  Separator as AriaSeparator,
} from 'react-aria-components';

import { cx } from '@/utils/cx';

export interface ColumnHeaderMenuTriggerProps {
  /** Column label for accessibility */
  columnLabel: string;
  /** Current sort direction for this column */
  sortDirection?: 'asc' | 'desc' | null;
  /** Whether this is the leftmost movable column */
  isFirst: boolean;
  /** Whether this is the rightmost movable column */
  isLast: boolean;
  /** Whether this column is pinned (disables move/hide with explanation) */
  isPinned?: boolean;
  /** Whether this column supports sorting; when false, Sort items are omitted */
  canSort?: boolean;
  /** Callback when sort is requested */
  onSort: (direction: 'asc' | 'desc') => void;
  /** Callback when move left is requested */
  onMoveLeft: () => void;
  /** Callback when move right is requested */
  onMoveRight: () => void;
  /** Callback when hide is requested */
  onHide: () => void;
}

/**
 * Small chevron button that opens a dropdown menu for column actions.
 * Shows chevron up when sorted ascending, chevron down otherwise.
 * Positioned on the far right of the column header.
 * Stops pointer event propagation to avoid triggering parent drag handlers.
 */
export function ColumnHeaderMenuTrigger({
  columnLabel,
  sortDirection,
  isFirst,
  isLast,
  isPinned = false,
  canSort = true,
  onSort,
  onMoveLeft,
  onMoveRight,
  onHide,
}: ColumnHeaderMenuTriggerProps) {
  const handleAction = useCallback(
    (key: React.Key) => {
      switch (key) {
        case 'sort-asc':
          onSort('asc');
          break;
        case 'sort-desc':
          onSort('desc');
          break;
        case 'move-left':
          // Safety check: don't move if pinned OR if this is the leftmost column
          if (!isPinned && !isFirst) {
            onMoveLeft();
          }
          break;
        case 'move-right':
          // Safety check: don't move if pinned OR if this is the rightmost column
          if (!isPinned && !isLast) {
            onMoveRight();
          }
          break;
        case 'hide':
          if (!isPinned) {
            onHide();
          }
          break;
      }
    },
    [onSort, onMoveLeft, onMoveRight, onHide, isPinned, isFirst, isLast]
  );

  // Build disabled keys based on position and pinned state
  const disabledKeys: string[] = [];
  if (isPinned) {
    // Pinned columns can't be moved or hidden
    disabledKeys.push('move-left', 'move-right', 'hide');
  } else {
    if (isFirst) {
      disabledKeys.push('move-left');
    }
    if (isLast) {
      disabledKeys.push('move-right');
    }
  }

  // Show chevron up when sorted ascending, chevron down otherwise
  const isSorted = sortDirection === 'asc' || sortDirection === 'desc';

  return (
    <AriaMenuTrigger>
      <AriaButton
        aria-label={`${columnLabel} column options`}
        className={cx(
          'p-0.5 rounded cursor-pointer shrink-0 ml-auto mr-2',
          'transition-all duration-100',
          'outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-[-2px]',
          isSorted
            ? // When sorted, always show the chevron in dark brand color (matches primary button)
              'text-[var(--color-fg-brand-primary)]'
            : // When not sorted, show on hover (desktop) or always (touch devices)
              'text-fg-quaternary hover:text-fg-secondary hover:bg-[var(--color-bg-tertiary)] opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100'
        )}
        // Stop propagation to prevent triggering dnd-kit drag handlers
        onPointerDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        {sortDirection === 'asc' ? (
          <ChevronUp className='size-3.5' />
        ) : (
          <ChevronDown className='size-3.5' />
        )}
      </AriaButton>

      <AriaPopover
        placement='bottom start'
        className={cx(
          'w-36 overflow-hidden rounded-md bg-primary shadow-lg ring-1 ring-secondary_alt',
          'origin-(--trigger-anchor-point) will-change-transform',
          'entering:duration-150 entering:ease-out entering:animate-in entering:fade-in entering:slide-in-from-top-0.5',
          'exiting:duration-100 exiting:ease-in exiting:animate-out exiting:fade-out exiting:slide-out-to-top-0.5'
        )}
      >
        <AriaMenu
          aria-label={`${columnLabel} column actions`}
          onAction={handleAction}
          disabledKeys={disabledKeys}
          className='py-0.5 outline-none'
        >
          {/* Sort options */}
          {canSort && (
            <>
              <AriaMenuItem
                id='sort-asc'
                textValue='Sort ascending'
                className={cx(
                  'px-1 outline-none cursor-pointer',
                  'focus:bg-primary_hover'
                )}
              >
                <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
                  <ArrowUp className='size-3.5 text-fg-quaternary shrink-0' />
                  <span className='flex-1 text-xs font-medium text-[var(--color-text-secondary)]'>
                    Sort ascending
                  </span>
                  {sortDirection === 'asc' && (
                    <Check className='size-3 text-[var(--color-fg-brand-primary)] shrink-0' />
                  )}
                </div>
              </AriaMenuItem>

              <AriaMenuItem
                id='sort-desc'
                textValue='Sort descending'
                className={cx(
                  'px-1 outline-none cursor-pointer',
                  'focus:bg-primary_hover'
                )}
              >
                <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
                  <ArrowDown className='size-3.5 text-fg-quaternary shrink-0' />
                  <span className='flex-1 text-xs font-medium text-[var(--color-text-secondary)]'>
                    Sort descending
                  </span>
                  {sortDirection === 'desc' && (
                    <Check className='size-3 text-[var(--color-fg-brand-primary)] shrink-0' />
                  )}
                </div>
              </AriaMenuItem>
            </>
          )}

          {/* Pinned column notice - shown instead of move/hide when pinned */}
          {isPinned ? (
            <>
              <AriaSeparator className='my-0.5 h-px bg-border-secondary' />
              <AriaMenuItem
                id='pinned-notice'
                textValue='This column is pinned'
                isDisabled
                className='px-1 outline-none cursor-default'
              >
                <div className='px-2 py-1.5'>
                  <span className='text-xs text-[var(--color-text-tertiary)]'>
                    This column is pinned. It can’t be moved or hidden.
                  </span>
                </div>
              </AriaMenuItem>
            </>
          ) : (
            <>
              <AriaSeparator className='my-0.5 h-px bg-border-secondary' />

              {/* Move options */}
              <AriaMenuItem
                id='move-left'
                textValue='Move left'
                className={state =>
                  cx(
                    'px-1 outline-none',
                    state.isDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                    !state.isDisabled && 'focus:bg-primary_hover'
                  )
                }
              >
                {({ isDisabled }) => (
                  <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
                    <ArrowLeft
                      className={cx(
                        'size-3.5 shrink-0',
                        isDisabled ? 'text-fg-disabled' : 'text-fg-quaternary'
                      )}
                    />
                    <span
                      className={cx(
                        'flex-1 text-xs font-medium',
                        isDisabled
                          ? 'text-[var(--color-text-tertiary)]'
                          : 'text-[var(--color-text-secondary)]'
                      )}
                    >
                      Move left
                    </span>
                  </div>
                )}
              </AriaMenuItem>

              <AriaMenuItem
                id='move-right'
                textValue='Move right'
                className={state =>
                  cx(
                    'px-1 outline-none',
                    state.isDisabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                    !state.isDisabled && 'focus:bg-primary_hover'
                  )
                }
              >
                {({ isDisabled }) => (
                  <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
                    <ArrowRight
                      className={cx(
                        'size-3.5 shrink-0',
                        isDisabled ? 'text-fg-disabled' : 'text-fg-quaternary'
                      )}
                    />
                    <span
                      className={cx(
                        'flex-1 text-xs font-medium',
                        isDisabled
                          ? 'text-[var(--color-text-tertiary)]'
                          : 'text-[var(--color-text-secondary)]'
                      )}
                    >
                      Move right
                    </span>
                  </div>
                )}
              </AriaMenuItem>

              <AriaSeparator className='my-0.5 h-px bg-border-secondary' />

              {/* Hide option */}
              <AriaMenuItem
                id='hide'
                textValue='Hide column'
                className={cx(
                  'px-1 outline-none cursor-pointer',
                  'focus:bg-primary_hover'
                )}
              >
                <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
                  <EyeOff className='size-3.5 text-fg-quaternary shrink-0' />
                  <span className='flex-1 text-xs font-medium text-[var(--color-text-secondary)]'>
                    Hide column
                  </span>
                </div>
              </AriaMenuItem>
            </>
          )}
        </AriaMenu>
      </AriaPopover>
    </AriaMenuTrigger>
  );
}
